import AppBar from '@material-ui/core/AppBar';
import { Button, SolvTheme, Stack } from '@solvhealth/jigsaw-v1';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import history from '../../../../core/history';
import useSetTitle from '../../../../hooks/useSetTitle';
import LoadingScreen from '../../../SolvPatternLibrary/LoadingScreen';
import PaperworkLayout from '../../PaperworkLayout';
import FinalizeCard from '~/components/Paperwork/components/Finalize/FinalizeCard';
import { SectionNumber, SECTIONS } from '../../constants';
import FinalizePhotoIdCard from '~/components/Paperwork/components/Finalize/FinalizePhotoIdCard';
import { PaperworkState } from '~/ducks/paperwork';
import useGetContentForSection from '../../components/Finalize/hooks/useGetContentForSection';
import usePushToPaperworkComplete from '../../components/Finalize/hooks/usePushToPaperworkComplete';
import useConsentSectionContent from '../../components/Finalize/hooks/useConsentSectionContent';
import { SectionPage } from '../../interfaces';
import { getFieldsWithDependenciesSatisified } from '../../util/fieldDependencies';
import {
  PaperworkFieldName,
  PaperworkSectionRoute,
  PAPERWORK_FIELD_ANSWER_YES,
} from '~/components/Paperwork/paperworkConstants';
import {
  paperworkHasCustomFields,
  paperworkHasVerificationAndReviewFields,
  paperworkHasHealthcareContactFields,
} from '~/components/Paperwork/util';
import { analyticsTrackEvent } from '~/core/analytics';
import { PAPERWORK_JUMP_TO_SECTION } from '~/core/analytics/events';
import { isPhotoIdUploadEnabledForEntrypoint } from '~/core/util/location';
import { useSetupSection } from '../../hooks/useSetupSection';
import { usePaperworkContextBookingId } from '~/routes/paperwork/PaperworkBookingIdContext';
import InvalidPhoneNumber from '../../InvalidPhoneNumber';
import AuthenticationContext from '~/core/login/AuthenticationContext';

const BottomBar = styled(AppBar).withConfig({ componentId: 'sc-1vhtas3-0' })`
  top: auto;
  bottom: 0;
  display: flex;
  flex-flow: row;
  flex-wrap: nowrap;
  justify-content: space-around;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
`;

const Spacer = styled.div.withConfig({ componentId: 'sc-1vhtas3-1' })`
  height: 100px;
  background: white;
  width: 100%;
`;

type Props = {
  location: LocationInterface;
  paperwork: PaperworkState;
  toggleFinalizeEditMode?: (...args: any[]) => any;
  setPageView?: (...args: any[]) => any;
};

const getSectionPagesFieldNames = (sectionNumber: SectionNumber) => {
  const sectionPages = SECTIONS.get(sectionNumber)?.pages ?? new Map();
  return [...sectionPages.values()].reduce(
    (allFieldNames: string[], page: SectionPage) => allFieldNames.concat(page.fieldNames),
    []
  );
};

const PaperworkFinalize = (props: Props) => {
  const { location, paperwork, toggleFinalizeEditMode, setPageView } = props;

  const bookingId = usePaperworkContextBookingId();
  const { isLoggedIn } = useContext(AuthenticationContext);

  const { t } = useTranslation(['paperwork']);
  const title = t('paperwork:finalize.htmlTitle');

  const { onGoBack, doesAccountMatchBooking } = useSetupSection(
    SectionNumber.Finalize,
    SectionNumber
  );

  const shouldRevealEmployerFields = () => {
    return (
      paperwork.formData[PaperworkFieldName.TREATED_AT_THE_REQUEST_OF_EMPLOYER] ===
      PAPERWORK_FIELD_ANSWER_YES
    );
  };

  const shouldRevealPaymentFields = () => {
    return paperwork.formData[PaperworkFieldName.SELF_PAY_CHECK] === PAPERWORK_FIELD_ANSWER_YES;
  };

  useSetTitle(title);

  const getContentForSection = useGetContentForSection('paperwork');
  const consentSectionContent = useConsentSectionContent(bookingId);
  const pushToPaperworkComplete = usePushToPaperworkComplete(bookingId);

  if (!paperwork?.standardFields) {
    return <LoadingScreen />;
  }

  const patientSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(SectionNumber.Patient),
    paperwork
  );

  const employerSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(SectionNumber.Employer),
    paperwork
  );

  const healthcareContactsFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(SectionNumber.HealthcareContacts),
    paperwork
  );

  const paymentSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(SectionNumber.Payment),
    paperwork
  );

  const verificationAndReviewSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(SectionNumber.VerificationAndReview),
    paperwork
  );

  const patientTitle = t(`paperwork:${SectionNumber.Patient}.sectionTitle` as any);
  const employerTitle = t(`paperwork:${SectionNumber.Employer}.sectionTitle` as any);
  const healthcareContactsTitle = t(
    `paperwork:${SectionNumber.HealthcareContacts}.sectionTitle` as any
  );
  const paymentTitle = t(`paperwork:${SectionNumber.Payment}.sectionTitle` as any);
  const customTitle = t(`paperwork:${SectionNumber.Custom}.sectionTitle` as any);
  const verificationAndReviewTitle = t(
    `paperwork:${SectionNumber.VerificationAndReview}.sectionTitle` as any
  );

  const handleOnClick = (pageNumber: any, route: any) => {
    analyticsTrackEvent(PAPERWORK_JUMP_TO_SECTION, {
      from: `${PaperworkSectionRoute.FINALIZE}/${bookingId}`,
      to: `/${route}/${bookingId}`,
      bookingId,
      isProviderGroupLocation: false,
      isLoggedIn,
    });

    setPageView?.(pageNumber);

    toggleFinalizeEditMode?.(true);
    history.push(`${route}/${bookingId}`);
  };

  if (!doesAccountMatchBooking) {
    return <InvalidPhoneNumber />;
  }

  return (
    <PaperworkLayout
      noPadding
      onGoBack={onGoBack}
      postTableContent={
        <>
          <Spacer />
          <BottomBar>
            <Stack flex="1" maxWidth={SolvTheme.breakpoints.sm} mx="auto" pb={3} pt={1} px={2}>
              <Button flex="1" fullWidth onClick={pushToPaperworkComplete}>
                {t('paperwork:finalize.submit')}
              </Button>
            </Stack>
          </BottomBar>
        </>
      }
      section={SectionNumber.VerificationAndReview}
      subtitle={t('paperwork:finalize.subtitle')}
      table
      title={t('paperwork:finalize.title')}
    >
      <FinalizeCard
        allFields={getContentForSection(patientSectionFieldNames)}
        handleOnClick={() => handleOnClick(0, PaperworkSectionRoute.PATIENT)}
        sectionTitle={patientTitle}
      />
      {isPhotoIdUploadEnabledForEntrypoint(location, 'paperwork') && <FinalizePhotoIdCard />}
      {shouldRevealEmployerFields() && (
        <FinalizeCard
          allFields={getContentForSection(employerSectionFieldNames)}
          handleOnClick={() => handleOnClick(0, PaperworkSectionRoute.EMPLOYER)}
          sectionTitle={employerTitle}
        />
      )}
      {paperworkHasHealthcareContactFields(paperwork) && (
        <FinalizeCard
          allFields={getContentForSection(healthcareContactsFieldNames)}
          handleOnClick={() => handleOnClick(0, PaperworkSectionRoute.HEALTHCARE_CONTACTS)}
          sectionTitle={healthcareContactsTitle}
        />
      )}
      {shouldRevealPaymentFields() && (
        <FinalizeCard
          allFields={getContentForSection(paymentSectionFieldNames)}
          handleOnClick={() => handleOnClick(0, PaperworkSectionRoute.PAYMENT)}
          sectionTitle={paymentTitle}
        />
      )}
      {paperworkHasCustomFields(paperwork) && (
        <FinalizeCard
          allFields={getContentForSection(null, true)}
          handleOnClick={() => handleOnClick(0, PaperworkSectionRoute.CUSTOM)}
          sectionTitle={customTitle}
        />
      )}
      {paperworkHasVerificationAndReviewFields(paperwork) && (
        <FinalizeCard
          allFields={getContentForSection(verificationAndReviewSectionFieldNames)}
          handleOnClick={() => handleOnClick(0, PaperworkSectionRoute.VERIFICATION_AND_REVIEW)}
          sectionTitle={verificationAndReviewTitle}
        />
      )}
      {!isEmpty(paperwork?.consentForms?.forms) && (
        <FinalizeCard
          allFields={consentSectionContent}
          handleOnClick={() => handleOnClick(0, PaperworkSectionRoute.CONSENT)}
          sectionTitle={t('paperwork:common.consentForms')}
        />
      )}
    </PaperworkLayout>
  );
};

export default PaperworkFinalize;
